import React, { useEffect, useState, useRef } from "react";
import { Users } from "lucide-react";
import api from "../lib/api";

/**
 * WatchingWithYou — horizontal avatar strip that shows the last N active
 * viewers on a live stream. Refreshes every 15s. Sends a viewer-heartbeat
 * every 20s so the current user shows up in other viewers' strips too.
 *
 * Backend contract:
 *  - `POST /api/live/{stream_id}/viewer-heartbeat` (auth) — upserts my row
 *  - `GET  /api/live/{stream_id}/viewers?limit=12` (public) — returns rows
 *    and total count of viewers active in last 45s.
 *
 * Falls back to a subtle "N watching" pill when unauthenticated or when
 * no other viewers are online yet — never hides completely so hosts get
 * the social-proof value even on quiet streams.
 */
export default function WatchingWithYou({ streamId, isAuthed = false, className = "" }) {
  const [viewers, setViewers] = useState([]);
  const [total, setTotal] = useState(0);
  const heartbeatRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!streamId) return undefined;
    let cancelled = false;
    const fetchViewers = async () => {
      try {
        const r = await api.get(`/live/${streamId}/viewers?limit=12`);
        if (cancelled) return;
        setViewers(Array.isArray(r?.data?.viewers) ? r.data.viewers : []);
        setTotal(Number(r?.data?.total) || 0);
      } catch { /* transient — try again next poll */ }
    };
    fetchViewers();
    pollRef.current = setInterval(fetchViewers, 15000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [streamId]);

  // Register presence — only when signed in. Anonymous viewers still get
  // to SEE the strip; they just don't appear in it (aligns with Twitch).
  useEffect(() => {
    if (!streamId || !isAuthed) return undefined;
    const beat = () => {
      api.post(`/live/${streamId}/viewer-heartbeat`).catch(() => { /* fine */ });
    };
    beat();
    heartbeatRef.current = setInterval(beat, 20000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [streamId, isAuthed]);

  if (total === 0 && viewers.length === 0) {
    return (
      <div className={`inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-2.5 py-1 ${className}`} data-testid="watching-with-you-empty">
        <Users className="h-3 w-3 text-white/70" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">Be the first</span>
      </div>
    );
  }

  const displayed = viewers.slice(0, 6);
  const extra = Math.max(0, total - displayed.length);

  return (
    <div
      data-testid="watching-with-you-strip"
      className={`inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur pl-1.5 pr-2.5 py-1 ${className}`}
    >
      <div className="flex -space-x-1.5">
        {displayed.map((v) => (
          <img
            key={v.user_id}
            src={v.user_avatar || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(v.user_name || v.user_id)}`}
            alt={v.user_name || "Viewer"}
            title={v.user_name || "Viewer"}
            className="h-5 w-5 rounded-full border border-white/90 object-cover bg-neutral-800"
            data-testid="watching-with-you-avatar"
            loading="lazy"
            onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(v.user_name || v.user_id)}`; }}
          />
        ))}
      </div>
      <span className="text-[11px] font-semibold text-white/90">
        {extra > 0 ? (
          <>+ {compact(extra)} watching</>
        ) : (
          <>{compact(total)} watching</>
        )}
      </span>
    </div>
  );
}

function compact(n) {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  if (n < 1000000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
}
